import React from 'react'
import {
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
} from 'recharts'
import './DashboardHalfDonutChart.style.css'

function HalfDonutTooltipContent({
  active,
  payload,
  valueFormatter,
}: {
  active?: boolean
  payload?: Array<{ name: string; value: number; payload?: { color?: string } }>
  valueFormatter: (v: number) => string
}): React.ReactElement | null {
  if (!active || !payload?.length) return null
  const item = payload[0]
  return (
    <div
      className="dashboard-half-donut-tooltip"
      style={{
        padding: '8px 12px',
        fontSize: 12,
        borderRadius: 8,
        border: '1px solid var(--gray-200)',
        background: 'var(--gray-white)',
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{item.name}</div>
      <div>{valueFormatter(item.value)}</div>
    </div>
  )
}

export type DashboardHalfDonutSlice = {
  name: string
  value: number
  color: string
}

type DashboardHalfDonutChartProps = {
  title: string
  /** Actual total (e.g. 31.2 for $31.2K). Used for metric display and segment math. */
  primaryValue: number
  /** Target total (e.g. 50.6 for $50.6K). Third segment = secondaryValue - primaryValue (blue-100). */
  secondaryValue: number
  /** Share of primary that is Dentist (0–1). Rest is Hygienist. Ignored when slices is provided. */
  dentistRatio?: number
  /** Optional formatter for metric (e.g. (v) => `$${v}K` or (v) => `${v}%`). */
  valueFormatter?: (value: number) => string
  /** Custom pie segments (e.g. for acceptance charts). When set, dentistRatio is ignored. */
  slices?: DashboardHalfDonutSlice[]
  /** When true, do not render the center title/value (e.g. title + "70% / 30%"). */
  hideCenterMetric?: boolean
  /** Separator between primary and secondary in center text (e.g. " / " or " vs "). Default " / ". */
  valueSeparator?: string
  /** Recharts canvas width. Use with chartHeight for compact embedded layouts. */
  chartWidth?: number
  /** Recharts canvas height. Half-donut arc height matches this when cy is at 100%. */
  chartHeight?: number
  innerRadius?: string | number
  outerRadius?: string | number
  /** Fill parent width; height follows 2:1 aspect ratio (Overview Production columns). */
  fluidSize?: boolean
  /** Gap between pie segments (degrees). Figma production distribution uses visible separation. */
  paddingAngle?: number
  className?: string
}

const defaultFormatter = (v: number): string =>
  v >= 1000 ? `$${(v / 1000).toFixed(1)}K` : `$${v}`

function resolveInnerRadius(
  inner: string | number,
  outerPx: number,
): string | number {
  if (typeof inner === 'number') return inner
  if (typeof inner === 'string' && inner.endsWith('%')) {
    const pct = Number.parseFloat(inner) / 100
    return Math.round(outerPx * pct)
  }
  return inner
}

function DashboardHalfDonutChart({
  title,
  primaryValue,
  secondaryValue,
  dentistRatio = 0.5,
  valueFormatter = defaultFormatter,
  slices: customSlices,
  hideCenterMetric = false,
  valueSeparator = ' / ',
  chartWidth = 272,
  chartHeight = 272,
  innerRadius = '55%',
  outerRadius = '100%',
  fluidSize = false,
  paddingAngle = 0,
  className = '',
}: DashboardHalfDonutChartProps): React.ReactElement {
  const canvasWidth = chartWidth
  const canvasHeight = hideCenterMetric
    ? Math.round(chartHeight / 2)
    : chartHeight
  const pieOuterRadius: string | number = hideCenterMetric
    ? canvasHeight
    : outerRadius
  const pieInnerRadius: string | number =
    hideCenterMetric && typeof pieOuterRadius === 'number'
      ? resolveInnerRadius(innerRadius, pieOuterRadius)
      : innerRadius

  const data: DashboardHalfDonutSlice[] = customSlices
    ? customSlices
    : (() => {
        const hygienistRatio = 1 - dentistRatio
        const dentistValue = primaryValue * dentistRatio
        const hygienistValue = primaryValue * hygienistRatio
        const remainderValue = Math.max(0, secondaryValue - primaryValue)
        return [
          {
            name: 'Hygienist',
            value: hygienistValue,
            color: 'var(--indigo-400)',
          },
          { name: 'Dentist', value: dentistValue, color: 'var(--indigo-300)' },
          {
            name: 'Remainder',
            value: remainderValue,
            color: 'var(--blue-100)',
          },
        ]
      })()

  return (
    <div
      className={`dashboard-half-donut${hideCenterMetric ? ' dashboard-half-donut--embedded' : ''} ${className}`.trim()}
    >
      {!hideCenterMetric && (
        <div className="dashboard-half-donut-metric">
          <span className="dashboard-half-donut-metric-label">{title}</span>
          <span className="dashboard-half-donut-metric-value">
            {valueFormatter(primaryValue)}
            {valueSeparator}
            {valueFormatter(secondaryValue)}
          </span>
        </div>
      )}
      <div
        className={`dashboard-half-donut-chart-wrapper${fluidSize ? ' dashboard-half-donut-chart-wrapper--fluid' : ''}`}
        style={
          fluidSize
            ? undefined
            : {
                width: canvasWidth,
                minWidth: canvasWidth,
                maxWidth: canvasWidth,
                height: canvasHeight,
              }
        }
      >
        <ResponsiveContainer
          width={fluidSize ? '100%' : canvasWidth}
          height={fluidSize ? '100%' : canvasHeight}
        >
          <RechartsPieChart
            margin={
              hideCenterMetric
                ? { top: 0, right: 0, bottom: 0, left: 0 }
                : undefined
            }
          >
            <Tooltip
              content={
                <HalfDonutTooltipContent valueFormatter={valueFormatter} />
              }
              cursor={{ fill: 'transparent' }}
            />
            <Pie
              data={data}
              dataKey="value"
              startAngle={180}
              endAngle={0}
              cy="100%"
              innerRadius={pieInnerRadius}
              outerRadius={pieOuterRadius}
              stroke="none"
              paddingAngle={paddingAngle}
              cornerRadius={8}
            >
              {data.map((slice, index) => (
                <Cell key={slice.name ?? index} fill={slice.color} />
              ))}
            </Pie>
          </RechartsPieChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}

export default DashboardHalfDonutChart
